import React from 'react';
import { Check, Calculator, Percent } from 'lucide-react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { CurrencySelect } from '../../../components/CurrencySelect';
import { cn } from '../../../utils/cn';
import type { UserProfile } from '../../../types';
import type { SplitMode } from '../../../hooks/useGroupDetails';

interface AddExpenseModalProps {
    isOpen: boolean;
    onClose: () => void;
    isEditing: boolean;

    description: string;
    setDescription: (value: string) => void;
    amount: string;
    setAmount: (value: string) => void;
    currency: string;
    setCurrency: (value: string) => void;
    date: string;
    setDate: (value: string) => void;

    members: UserProfile[];
    selectedMemberIds: string[];
    toggleMemberSelection: (uid: string) => void;

    splitMode: SplitMode;
    setSplitMode: (mode: SplitMode) => void;
    exactAmounts: Record<string, string>;
    setExactAmounts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    percentages: Record<string, string>;
    setPercentages: React.Dispatch<React.SetStateAction<Record<string, string>>>;

    onSubmit: (e?: React.SyntheticEvent<HTMLFormElement>) => Promise<void>;
    onDelete?: () => void;
}

export const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
    isOpen,
    onClose,
    isEditing,
    description, setDescription,
    amount, setAmount,
    currency, setCurrency,
    date, setDate,
    members,
    selectedMemberIds, toggleMemberSelection,
    splitMode, setSplitMode,
    exactAmounts, setExactAmounts,
    percentages, setPercentages,
    onSubmit,
    onDelete
}) => {
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEditing ? "Edit Expense" : "Add Expense"}
        >
            <form onSubmit={onSubmit} className="space-y-4">
                <Input
                    label="Description"
                    placeholder="e.g. Groceries"
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setDescription(e.target.value); }}
                />
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Amount"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setAmount(e.target.value); }}
                    />
                    <CurrencySelect
                        label="Currency"
                        value={currency}
                        onChange={setCurrency}
                    />
                </div>
                <Input
                    label="Date"
                    type="date"
                    value={date}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setDate(e.target.value); }}
                />

                <div className="space-y-3">
                    <p className="text-sm font-bold text-text-body">Split With</p>
                    <div className="grid gap-2">
                        {members.map((profile) => (
                            <div key={profile.uid} className="flex items-center justify-between gap-3">
                                <button
                                    type="button"
                                    onClick={() => { toggleMemberSelection(profile.uid); }}
                                    className="flex flex-1 items-center gap-2 text-left"
                                >
                                    <div className={cn(
                                        "flex h-5 w-5 items-center justify-center rounded border transition-colors",
                                        selectedMemberIds.includes(profile.uid) ? "bg-primary-600 border-primary-600" : "border-border-strong bg-bg-card"
                                    )}>
                                        {selectedMemberIds.includes(profile.uid) && <Check size={14} className="text-white" />}
                                    </div>
                                    <span className={cn(
                                        "text-sm font-medium",
                                        selectedMemberIds.includes(profile.uid) ? "text-text-heading" : "text-text-faint"
                                    )}>
                                        {profile.displayName}
                                    </span>
                                </button>

                                {selectedMemberIds.includes(profile.uid) && splitMode !== 'equal' && (
                                    <div className="w-24">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            placeholder={splitMode === 'exact' ? "0.00" : "0"}
                                            className="h-8 py-1 text-right text-xs"
                                            value={splitMode === 'exact' ? (exactAmounts[profile.uid] ?? '') : (percentages[profile.uid] ?? '')}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const val = e.target.value;
                                                if (splitMode === 'exact') setExactAmounts(prev => ({ ...prev, [profile.uid]: val }));
                                                else setPercentages(prev => ({ ...prev, [profile.uid]: val }));
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex gap-1 rounded-lg bg-bg-card-alt p-1">
                    {(['equal', 'exact', 'percentage'] as SplitMode[]).map((mode) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => { setSplitMode(mode); }}
                            className={cn(
                                "flex-1 rounded-md py-1.5 text-[10px] font-bold transition-all uppercase tracking-wider",
                                splitMode === mode ? "bg-bg-card text-primary-600 shadow-sm" : "text-text-muted hover:text-text-body"
                            )}
                        >
                            {mode === 'equal' && <Check className="inline-block mr-1 h-3 w-3" />}
                            {mode === 'exact' && <Calculator className="inline-block mr-1 h-3 w-3" />}
                            {mode === 'percentage' && <Percent className="inline-block mr-1 h-3 w-3" />}
                            {mode}
                        </button>
                    ))}
                </div>

                <div className="flex gap-2">
                    {isEditing && onDelete && (
                        <Button type="button" variant="danger" className="flex-1" onClick={onDelete}>
                            Delete
                        </Button>
                    )}
                    <Button type="submit" className={cn(isEditing ? "flex-1" : "w-full")}>
                        {isEditing ? "Update Expense" : "Save Expense"}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
